import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  useImportZohoOrders,
  useGetLastOrderImport,
  useUpdateLineItemQuantity,
  useGenerateLabelsFile,
  useGetLabelForecast,
  useDeleteOrder,
  useDeleteOrderLine,
  useCompleteOrder,
  useSyncB2bOrderStages,
  useSetOrdersReadyToPick,
} from '../hooks/backend/scanPage'
import { usePostToteLabels, useGetCustomerLabelData } from '../hooks/backend/customersPage'
import {
  useGetAllOrderLinesCached,
  useGetOrderPicklistCached,
  useGetUnpickedItemsCached,
  useGetProductsCached,
} from '../hooks/cachedQueries'
import ProductFormDialog from './ui/ProductFormDialog'
import type { Product } from './ui/ProductTable'
import { PRODUCTS_CHANGED } from '../utils/hubSearchEvents'
import { generatePicklistPdf, type PicklistData } from '../utils/picklistPdf'
import { generatePicklistLabelPdf } from '../utils/picklistLabelPdf'
import { getPicklistSize } from '../utils/picklistSettings'
import { generateUnpickedItemsPdf, type UnpickedItem } from '../utils/unpickedItemsPdf'
import {
  ORDERS_IMPORTED_EVENT,
  announceOrdersImported,
  describeRun,
  timeAgo,
  type LastOrderImport,
  type OrderImportResult,
} from '../utils/orderImport'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { Button } from '../lib/shadcn/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../lib/shadcn/tooltip'
import { Badge } from '../lib/shadcn/badge'
import { Checkbox } from '../lib/shadcn/checkbox'
import { Input } from '../lib/shadcn/input'
import { Label } from '../lib/shadcn/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../lib/shadcn/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../lib/shadcn/table'
import { cn } from '../lib/shadcn/utils'
import { RefreshCw, Filter, ArrowRight, Trash2, ChevronRight, ChevronDown, FileText, Loader2, ListFilter, CheckCircle, QrCode, MoreHorizontal } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../lib/shadcn/dropdown-menu'
import { toast } from '../lib/shadcn/sonner'
import { useOnRestored, useRestoreItem, useUndoToast } from '../hooks/useUndoToast'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../lib/shadcn/alert-dialog'
import { HelpText } from '../components/HelpText'

// ─── Types ────────────────────────────────────────────────────────────────────

interface OrderLine {
  id?: string | number
  order_id: string
  item_name?: string
  item_sku?: string
  quantity_ordered: number
  quantity_printed: number
  quantity_picked: number
  customer?: string
  customer_id?: string | null
  order_status?: string | null
  delivery_method?: string | null
  shipment_count?: number | string
  /** When the order was marked Ready to pick (null = held as Printed). */
  ready_to_pick_at?: string | null
}

type EditableField = 'quantity_ordered' | 'quantity_printed' | 'quantity_picked'

interface LabelForecast {
  week: { weekStart: string; weekEnd: string; generatedAt: string } | null
  /** The forecast's week has already started, so it has no bags. */
  stale?: boolean
  items: { sku: string; item_name: string | null; quantity: number }[]
  total: number
  printed: { printed_at: string; printed_by: string | null; total_bags: number; print_count: number } | null
}

function formatForecastWeek(week: { weekStart: string; weekEnd: string }): string {
  const fmt = (d: string) =>
    new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  return `${fmt(week.weekStart)} – ${fmt(week.weekEnd)}`
}

interface OrderSummary {
  order_id: string
  customer: string
  customer_id: string | null
  total_items: number
  /** Total product weight of the ordered bags, in lbs (null until products load). */
  weight_lbs: number | null
  /** Ordered bags whose product has no weight, so weight_lbs is short. */
  bags_without_weight: number
  quantity_printed: number
  quantity_picked: number
  quantity_remaining: number
  progress: number
  /**
   * Printed = labels printed, held off the Scan page. Ready to Pick = released
   * to the Scan page (Orders page button), so a new label run can wait while
   * the last run is still being packed.
   */
  status: 'Complete' | 'Printed' | 'Ready to Pick' | 'In Progress' | 'Not Started'
  shipment_count: number
  isShippingCustomer: boolean
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function computeOrderSummaries(
  lines: OrderLine[],
  weightOzBySku: Map<string, number> | null,
): OrderSummary[] {
  const map = new Map<
    string,
    {
      total: number
      weightOz: number
      noWeight: number
      printed: number
      picked: number
      customer: string
      customer_id: string | null
      complete: boolean
      ready: boolean
      shipment_count: number
      isShippingCustomer: boolean
    }
  >()

  for (const line of lines) {
    const key = String(line.order_id)
    const entry = map.get(key) ?? {
      total: 0,
      weightOz: 0,
      noWeight: 0,
      printed: 0,
      picked: 0,
      customer: line.customer ?? '',
      customer_id: line.customer_id ?? null,
      complete: false,
      ready: false,
      shipment_count: Number(line.shipment_count ?? 0),
      isShippingCustomer: (line.delivery_method ?? 'shipping') === 'shipping',
    }
    entry.total += Number(line.quantity_ordered ?? 0)
    if (weightOzBySku) {
      const oz = weightOzBySku.get((line.item_sku ?? '').trim().toLowerCase())
      if (oz != null) entry.weightOz += oz * Number(line.quantity_ordered ?? 0)
      else entry.noWeight += Number(line.quantity_ordered ?? 0)
    }
    entry.printed += Number(line.quantity_printed ?? 0)
    entry.picked += Number(line.quantity_picked ?? 0)
    if (line.ready_to_pick_at) entry.ready = true
    if (['complete', 'shipped', 'delivered'].includes(String(line.order_status ?? '').toLowerCase())) entry.complete = true
    map.set(key, entry)
  }

  return Array.from(map.entries()).map(
    ([order_id, { total, weightOz, noWeight, printed, picked, customer, customer_id, complete, ready, shipment_count, isShippingCustomer }]) => {
      const remaining = Math.max(0, total - picked)
      const progress = complete ? 100 : total > 0 ? Math.round((picked / total) * 100) : 0
      const status: OrderSummary['status'] =
        complete || (total > 0 && picked >= total)
          ? 'Complete'
          : picked > 0
            ? 'In Progress'
            : total > 0 && printed >= total
              ? ready
                ? 'Ready to Pick'
                : 'Printed'
              : 'Not Started'
      return {
        order_id,
        customer,
        customer_id,
        total_items: total,
        weight_lbs: weightOzBySku ? weightOz / 16 : null,
        bags_without_weight: noWeight,
        quantity_printed: printed,
        quantity_picked: picked,
        quantity_remaining: remaining,
        progress,
        status,
        shipment_count,
        isShippingCustomer,
      }
    }
  )
}

// ─── Progress bar cell ────────────────────────────────────────────────────────

function ProgressBar({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-2 min-w-[64px]">
      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            value === 100 ? 'bg-primary' : value === 0 ? 'bg-muted-foreground/30' : 'bg-yellow-500 dark:bg-yellow-400'
          )}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground w-8 text-right">{value}%</span>
    </div>
  )
}

// ─── Editable quantity cell ─────────────────────────────────────────────────

function EditableQtyCell({
  value,
  saving,
  onSave,
}: {
  value: number
  saving: boolean
  onSave: (next: number) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(String(value))

  const commit = () => {
    setEditing(false)
    const next = Number(draft)
    if (!Number.isFinite(next) || next < 0 || next === value) {
      setDraft(String(value))
      return
    }
    onSave(next)
  }

  if (editing) {
    return (
      <Input
        autoFocus
        type="number"
        min={0}
        value={draft}
        disabled={saving}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit()
          if (e.key === 'Escape') {
            setDraft(String(value))
            setEditing(false)
          }
        }}
        className="h-7 w-16 ml-auto text-right text-sm"
      />
    )
  }

  return (
    <button
      type="button"
      onClick={() => {
        setDraft(String(value))
        setEditing(true)
      }}
      disabled={saving}
      className={cn(
        'w-full text-right px-2 py-1 rounded hover:bg-accent transition-colors cursor-text tabular-nums',
        saving && 'opacity-50'
      )}
    >
      {value}
    </button>
  )
}

// ─── Line items detail table ────────────────────────────────────────────────

function OrderLineItems({
  lines,
  savingKey,
  deletingLineId,
  onUpdate,
  onDelete,
  onOpenProduct,
  openingSku,
  weightOzBySku,
}: {
  lines: OrderLine[]
  savingKey: string | null
  deletingLineId: string | number | null
  onUpdate: (line: OrderLine, field: EditableField, value: number) => void
  onDelete: (line: OrderLine) => void
  onOpenProduct: (line: OrderLine) => void
  openingSku: string | null
  weightOzBySku: Map<string, number> | null
}) {
  if (lines.length === 0) {
    return (
      <div className="px-6 py-4 text-sm text-muted-foreground bg-muted/30">
        No line items for this order.
      </div>
    )
  }

  // Keep completed line items at the bottom
  const sortedLines = [...lines].sort((a, b) => {
    const aDone = Number(a.quantity_picked ?? 0) >= Number(a.quantity_ordered ?? 0) ? 1 : 0
    const bDone = Number(b.quantity_picked ?? 0) >= Number(b.quantity_ordered ?? 0) ? 1 : 0
    return aDone - bDone
  })

  return (
    <div className="bg-muted/30 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
        Line Items
      </p>
      <div className="rounded-md border bg-background overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">Item</TableHead>
              <TableHead className="whitespace-nowrap">SKU</TableHead>
              <TableHead className="whitespace-nowrap text-right">Ordered</TableHead>
              <TableHead className="whitespace-nowrap text-right">Weight (lbs)</TableHead>
              <TableHead className="whitespace-nowrap text-right">Printed</TableHead>
              <TableHead className="whitespace-nowrap text-right">Picked</TableHead>
              <TableHead className="whitespace-nowrap text-right">Remaining</TableHead>
              <TableHead className="whitespace-nowrap">Status</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedLines.map((line, i) => {
              const ordered = Number(line.quantity_ordered ?? 0)
              const printed = Number(line.quantity_printed ?? 0)
              const picked = Number(line.quantity_picked ?? 0)
              const remaining = Math.max(0, ordered - picked)
              const bagOz = weightOzBySku?.get((line.item_sku ?? '').trim().toLowerCase())
              const rowKey = String(line.id ?? `${line.order_id}-${i}`)
              const status: OrderSummary['status'] =
                picked >= ordered ? 'Complete' : picked === 0 ? 'Not Started' : 'In Progress'
              return (
                <TableRow
                  key={rowKey}
                  className={cn(remaining === 0 && 'bg-green-50 dark:bg-green-950/30')}
                >
                  <TableCell className="text-foreground">
                    {line.item_sku ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 text-left underline decoration-dotted decoration-muted-foreground/60 underline-offset-4 hover:decoration-solid hover:decoration-foreground focus-visible:decoration-solid focus-visible:outline-none"
                        title="Open product details"
                        onClick={() => onOpenProduct(line)}
                      >
                        {line.item_name || line.item_sku}
                        {openingSku === line.item_sku && (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                        )}
                      </button>
                    ) : (
                      line.item_name || '—'
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{line.item_sku || '—'}</TableCell>
                  <TableCell className="text-right p-1">
                    <EditableQtyCell
                      value={ordered}
                      saving={savingKey === `${rowKey}:quantity_ordered`}
                      onSave={next => onUpdate(line, 'quantity_ordered', next)}
                    />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {bagOz != null ? (
                      (ordered * bagOz / 16).toFixed(1)
                    ) : weightOzBySku ? (
                      <span className="text-amber-600 dark:text-amber-400" title="No weight set on this product">No weight</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right p-1">
                    <EditableQtyCell
                      value={printed}
                      saving={savingKey === `${rowKey}:quantity_printed`}
                      onSave={next => onUpdate(line, 'quantity_printed', next)}
                    />
                  </TableCell>
                  <TableCell className="text-right p-1">
                    <EditableQtyCell
                      value={picked}
                      saving={savingKey === `${rowKey}:quantity_picked`}
                      onSave={next => onUpdate(line, 'quantity_picked', next)}
                    />
                  </TableCell>
                  <TableCell className={cn('text-right', remaining === 0 && 'text-muted-foreground')}>{remaining}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        status === 'Complete'
                          ? 'default'
                          : status === 'Not Started'
                            ? 'outline'
                            : 'secondary'
                      }
                    >
                      {status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
  <Button
    variant="ghost"
    size="sm"
    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
    disabled={
      line.id === undefined ||
      deletingLineId === line.id
    }
    aria-label={`Delete ${line.item_name || 'line item'}`}
    onClick={() => onDelete(line)}
  >
    {deletingLineId === line.id ? (
      <Loader2 className="w-3.5 h-3.5 animate-spin" />
    ) : (
      <Trash2 className="w-3.5 h-3.5" />
    )}
  </Button>
</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const ORDER_VIEW_KEY = 'poppin.orders.view.v1'
const ALL_STATUSES: OrderSummary['status'][] = ['Not Started', 'Printed', 'Ready to Pick', 'In Progress', 'Complete']
const ACTIVE_STATUSES = ALL_STATUSES.filter(status => status !== 'Complete')
function readOrderView() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(ORDER_VIEW_KEY) ?? '{}')
    return {
      filter: typeof saved.filter === 'string' ? saved.filter : '',
      statuses: Array.isArray(saved.statuses) ? ALL_STATUSES.filter(status => saved.statuses.includes(status)) : ACTIVE_STATUSES,
      sorting: Array.isArray(saved.sorting) ? saved.sorting.filter((sort: any) => typeof sort?.id === 'string' && typeof sort.desc === 'boolean') as SortingState : [],
      expanded: typeof saved.expanded === 'string' ? saved.expanded : null,
    }
  } catch {
    return { filter: '', statuses: ACTIVE_STATUSES, sorting: [] as SortingState, expanded: null }
  }
}

export default function OrdersPage() {
  const getAllOrderLines = useGetAllOrderLinesCached()
  const importZohoOrders = useImportZohoOrders()
  const getLastOrderImport = useGetLastOrderImport()
  const updateLineItemQuantity = useUpdateLineItemQuantity()
  const getOrderPicklist = useGetOrderPicklistCached()
  const getUnpickedItems = useGetUnpickedItemsCached()
  const generateLabelsFile = useGenerateLabelsFile()
  const getLabelForecast = useGetLabelForecast()
  const deleteOrder = useDeleteOrder()
  const deleteOrderLine = useDeleteOrderLine()
  const getProducts = useGetProductsCached()

  // Clicking a line item opens that product's details.
  const [productTarget, setProductTarget] = useState<Product | null>(null)
  const [productOpen, setProductOpen] = useState(false)
  const [openingSku, setOpeningSku] = useState<string | null>(null)
  const handleOpenProduct = useCallback(
    async (line: OrderLine) => {
      const sku = (line.item_sku ?? '').trim()
      if (!sku) return
      const find = (list: Product[] | null | undefined) =>
        (list ?? []).find(p => (p.sku ?? '').trim().toLowerCase() === sku.toLowerCase()) ?? null
      setOpeningSku(line.item_sku ?? null)
      try {
        let product = find(getProducts.data as Product[] | null)
        if (!product) product = find((await getProducts.trigger()) as Product[] | null)
        if (!product) {
          toast.error(`No product with SKU ${sku}`, {
            description: 'It may have been renamed or removed in Products.',
          })
          return
        }
        setProductTarget(product)
        setProductOpen(true)
      } catch (err) {
        toast.error('Could not load the product', {
          description: err instanceof Error ? err.message : String(err),
        })
      } finally {
        setOpeningSku(null)
      }
    },
    [getProducts],
  )
  const completeOrder = useCompleteOrder()
  const showUndo = useUndoToast()
  const restoreItem = useRestoreItem()
  const syncB2bOrderStages = useSyncB2bOrderStages()
  const postToteLabels = usePostToteLabels()
  const getCustomerLabelData = useGetCustomerLabelData()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [savedView] = useState(readOrderView)
  const [sorting, setSorting] = useState<SortingState>(savedView.sorting)
  const [filterValue, setFilterValue] = useState(searchParams.get('customer') ?? savedView.filter)
  const [statusFilter, setStatusFilter] = useState<OrderSummary['status'][]>(savedView.statuses)
  const [importing, setImporting] = useState(false)
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(savedView.expanded)
  useEffect(() => {
    try { sessionStorage.setItem(ORDER_VIEW_KEY, JSON.stringify({ filter: filterValue, statuses: statusFilter, sorting, expanded: expandedOrderId })) } catch { /* Storage may be unavailable. */ }
  }, [filterValue, statusFilter, sorting, expandedOrderId])

  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [picklistOrderId, setPicklistOrderId] = useState<string | null>(null)
  const [unpickedPdfLoading, setUnpickedPdfLoading] = useState(false)
  const [labelsFileLoading, setLabelsFileLoading] = useState(false)
  // Generate Labels File dialog: optional forecast bags on top of the orders.
  const [labelsDialogOpen, setLabelsDialogOpen] = useState(false)
  const [includeForecast, setIncludeForecast] = useState(false)
  const [labelForecast, setLabelForecast] = useState<LabelForecast | null>(null)
  const [labelForecastLoading, setLabelForecastLoading] = useState(false)
  const [labelForecastError, setLabelForecastError] = useState<string | null>(null)
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
  const [deletingOrderId, setDeletingOrderId] = useState<string | null>(null)
  const [deletingLineId, setDeletingLineId] =
    useState<string | number | null>(null)
  const [orderToComplete, setOrderToComplete] = useState<string | null>(null)

  // Tote label dialog state.
  const [labelTarget, setLabelTarget] = useState<{ customerId: string; customer: string } | null>(null)
  const [labelCopies, setLabelCopies] = useState('1')
  const [loadingSuggestion, setLoadingSuggestion] = useState(false)
  const [printingLabels, setPrintingLabels] = useState(false)
  // Shipping orders print one tote label straight away (order id while printing).
  const [singleLabelOrderId, setSingleLabelOrderId] = useState<string | null>(null)

  const openToteLabelDialog = useCallback(
    async (customerId: string, customer: string) => {
      setLabelTarget({ customerId, customer })
      setLabelCopies('1')
      setLoadingSuggestion(true)
      try {
        const data = (await getCustomerLabelData.trigger({ customer_id: customerId })) as
          | { totes_needed?: number }
          | null
        const suggested = data?.totes_needed
        if (typeof suggested === 'number' && suggested > 0) {
          setLabelCopies(String(suggested))
        }
      } catch {
        // Keep the default of 1 if the suggestion fails to load.
      } finally {
        setLoadingSuggestion(false)
      }
    },
    [getCustomerLabelData]
  )

  const handlePrintToteLabels = useCallback(async () => {
    if (!labelTarget) return
    const count = Math.max(1, Math.round(Number(labelCopies) || 1))
    setPrintingLabels(true)
    try {
      const result = (await postToteLabels.trigger({
        customer_id: labelTarget.customerId,
        copies: count,
      })) as { success?: boolean; copies?: number } | null

      if (!result?.success || result.copies !== count) {
        throw new Error(
          `Expected ${count} tote labels, but the printing service reported ${result?.copies ?? 0}.`,
        )
      }

      toast.success(`${result.copies} tote labels submitted for ${labelTarget.customer || 'customer'}`)
      setLabelTarget(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send tote labels')
    } finally {
      setPrintingLabels(false)
    }
  }, [labelTarget, labelCopies, postToteLabels])

  // Shipping orders go out in one tote, so print a single label with no dialog.
  const handlePrintSingleToteLabel = useCallback(
    async (orderId: string, customerId: string, customer: string) => {
      setSingleLabelOrderId(orderId)
      try {
        const result = (await postToteLabels.trigger({
          customer_id: customerId,
          copies: 1,
        })) as { success?: boolean; copies?: number } | null
        if (!result?.success || result.copies !== 1) {
          throw new Error('The printing service did not accept the tote label.')
        }
        toast.success(`Tote label submitted for ${customer || 'customer'}`)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to send tote label')
      } finally {
        setSingleLabelOrderId(null)
      }
    },
    [postToteLabels]
  )

  const handleGeneratePicklist = useCallback(
    async (orderId: string) => {
      setPicklistOrderId(orderId)
      try {
        const data = (await getOrderPicklist.trigger({ orderId })) as PicklistData
        if (getPicklistSize() === 'label4x6') {
          await generatePicklistLabelPdf(data)
        } else {
          await generatePicklistPdf(data)
        }
        toast.success('Picklist downloaded')
      } catch (_e) {
        toast.error('Failed to generate picklist')
      } finally {
        setPicklistOrderId(null)
      }
    },
    [getOrderPicklist]
  )

  const handleGenerateUnpickedPdf = useCallback(async () => {
    setUnpickedPdfLoading(true)
    try {
      const items = (await getUnpickedItems.trigger(undefined, { skipCache: true })) as UnpickedItem[]
      await generateUnpickedItemsPdf(items ?? [])
      toast.success('Items to pick PDF opened')
    } catch (_e) {
      toast.error('Failed to generate PDF')
    } finally {
      setUnpickedPdfLoading(false)
    }
  }, [getUnpickedItems])

  const selectedOrderIds = useMemo(
    () => Object.keys(rowSelection).filter(id => rowSelection[id]),
    [rowSelection]
  )

  const handleUpdateLine = useCallback(
    async (line: OrderLine, field: EditableField, value: number) => {
      if (line.id === undefined) {
        toast.error('Cannot update: missing line item id')
        return
      }
      const key = `${String(line.id)}:${field}`
      setSavingKey(key)
      try {
        await updateLineItemQuantity.trigger({ id: line.id, field, value })
        await getAllOrderLines.trigger(undefined, { skipCache: true })
        toast.success('Updated')
      } catch (_e) {
        toast.error('Update failed')
      } finally {
        setSavingKey(null)
      }
    },
    [updateLineItemQuantity, getAllOrderLines]
  )

  useEffect(() => {
    getAllOrderLines.trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const customer = searchParams.get('customer')
    if (customer) {
      setFilterValue(customer)
      setStatusFilter(ALL_STATUSES)
      setExpandedOrderId(null)
    }
  }, [searchParams])

  // Opened from the header search: show just that order, whatever its status,
  // with its lines expanded.
  useEffect(() => {
    const order = searchParams.get('order')
    if (!order) return
    setFilterValue(order)
    setStatusFilter(['Not Started', 'Printed', 'Ready to Pick', 'In Progress', 'Complete'])
    setExpandedOrderId(order)
  }, [searchParams])

  // No "Are you sure?": the toast's Undo (and Settings > Recently deleted)
  // puts the line back.
  const handleDeleteLine = useCallback(async (line: OrderLine) => {
    if (line.id === undefined) {
      toast.error('Cannot delete: missing line item id')
      return
    }
    const itemName = line.item_name || line.item_sku || 'Line item'
    setDeletingLineId(line.id)
    try {
      const result = (await deleteOrderLine.trigger({
        id: line.id,
        order_id: line.order_id,
      })) as { trash_id?: number | null } | undefined
      // Force-refresh the cached order lines so totals, remaining quantities,
      // progress, and the expanded line-item table reflect the deletion.
      await getAllOrderLines.trigger(undefined, { skipCache: true })
      showUndo(`${itemName} removed from ${line.order_id}`, result?.trash_id)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete line item')
    } finally {
      setDeletingLineId(null)
    }
  }, [deleteOrderLine, getAllOrderLines, showUndo])

  // Reload after an order, line or unpicked items are put back.
  useOnRestored(['order', 'order_line', 'unpicked_lines'], () => {
    void Promise.resolve(getAllOrderLines.trigger(undefined, { skipCache: true })).catch(() => undefined)
  })

  const handleImportOrders = useCallback(async () => {
    setImporting(true)
    try {
      const result = (await importZohoOrders.trigger({ trigger: 'manual' })) as OrderImportResult
      if (!result.ran) {
        toast.info(result.skippedReason ?? 'Import skipped')
      } else {
        announceOrdersImported(result)
        const skipped = result.run?.skipped_lines ?? []
        if (skipped.length > 0) {
          toast.warning(
            `${describeRun(result.run)}. Left out ${skipped.length} line${skipped.length === 1 ? '' : 's'} with a SKU that isn't a Hub product: ${skipped.join('; ')}`,
            { duration: 15000 }
          )
        } else {
          toast.success(describeRun(result.run))
        }
      }
    } catch (e) {
      toast.error(e instanceof Error ? `Import failed: ${e.message}` : 'Import failed')
    } finally {
      setImporting(false)
      void Promise.resolve(getLastOrderImport.trigger()).catch(() => undefined)
    }
  }, [importZohoOrders, getLastOrderImport])

  // Reload when an import (this button, or the auto-import in the
  // background) brings in new or changed orders.
  const reloadLinesTrigger = getAllOrderLines.trigger
  const lastImportTrigger = getLastOrderImport.trigger
  useEffect(() => {
    const onImported = () => {
      void Promise.resolve(reloadLinesTrigger(undefined, { skipCache: true })).catch(() => undefined)
      void Promise.resolve(lastImportTrigger()).catch(() => undefined)
    }
    window.addEventListener(ORDERS_IMPORTED_EVENT, onImported)
    return () => window.removeEventListener(ORDERS_IMPORTED_EVENT, onImported)
  }, [reloadLinesTrigger, lastImportTrigger])

  // "Imported 5 min ago" under the button; refreshed every minute.
  const [, setClock] = useState(0)
  useEffect(() => {
    void Promise.resolve(lastImportTrigger()).catch(() => undefined)
    const t = window.setInterval(() => {
      setClock(c => c + 1)
      void Promise.resolve(lastImportTrigger()).catch(() => undefined)
    }, 60 * 1000)
    return () => window.clearInterval(t)
  }, [lastImportTrigger])
  const lastImport = getLastOrderImport.data as LastOrderImport | undefined
  const lastImportFailed = !!lastImport?.lastRun?.error

  const handleCompleteOrder = useCallback(async () => {
    if (orderToComplete === null) return
    try {
      const result = (await completeOrder.trigger({ order_id: orderToComplete })) as
        | { unpicked_trash_id?: number | null }
        | undefined
      await getAllOrderLines.trigger(undefined, { skipCache: true })
      const trashId = result?.unpicked_trash_id
      if (trashId) {
        toast.success(`Order ${orderToComplete} marked complete`, {
          duration: 10_000,
          description: 'Unpicked items were removed. They stay in Settings › Recently deleted for 30 days.',
          action: { label: 'Put items back', onClick: () => void restoreItem(trashId) },
        })
      } else {
        toast.success(`Order ${orderToComplete} marked complete`)
      }
    } catch (_e) {
      toast.error('Failed to complete order')
    } finally {
      setOrderToComplete(null)
    }
  }, [orderToComplete, completeOrder, getAllOrderLines, restoreItem])

  // No "Are you sure?": the toast's Undo (and Settings > Recently deleted)
  // puts the order and its lines back.
  const handleDeleteOrder = useCallback(async (orderId: string) => {
    setDeletingOrderId(orderId)
    try {
      const result = (await deleteOrder.trigger({ order_id: orderId })) as
        | { trash_id?: number | null }
        | undefined
      await getAllOrderLines.trigger(undefined, { skipCache: true })
      setExpandedOrderId(cur => (cur === orderId ? null : cur))
      showUndo(`Order ${orderId} deleted`, result?.trash_id)
    } catch (_e) {
      toast.error('Failed to delete order')
    } finally {
      setDeletingOrderId(null)
    }
  }, [deleteOrder, getAllOrderLines, showUndo])

  const allLines = useMemo<OrderLine[]>(
    () => (getAllOrderLines.data as OrderLine[] | undefined) ?? [],
    [getAllOrderLines.data]
  )

  // Bag weight per SKU for the order weight column: the measured (actual)
  // weight when set, since that's what the bags really weigh, else the
  // published label weight.
  const getProductsTrigger = getProducts.trigger
  useEffect(() => {
    void Promise.resolve(getProductsTrigger()).catch(() => undefined)
  }, [getProductsTrigger])
  const weightOzBySku = useMemo(() => {
    const products = getProducts.data as Product[] | null | undefined
    if (!products) return null
    const map = new Map<string, number>()
    for (const p of products) {
      const oz = Number(p.actual_weight_oz ?? p.weight_oz)
      if (p.sku && Number.isFinite(oz) && oz > 0) map.set(p.sku.trim().toLowerCase(), oz)
    }
    return map
  }, [getProducts.data])

  const summaries = useMemo<OrderSummary[]>(
    () => computeOrderSummaries(allLines, weightOzBySku),
    [allLines, weightOzBySku]
  )

  // Each time the orders load, move B2B order stages along to match (picks up
  // label printing and edits made outside the Hub's buttons). Runs in the
  // background; the page doesn't wait for it.
  const syncB2bTrigger = syncB2bOrderStages.trigger
  useEffect(() => {
    if (!getAllOrderLines.data) return
    syncB2bTrigger().catch(() => undefined)
  }, [getAllOrderLines.data, syncB2bTrigger])

  // Selected orders excluding those already complete.
  const selectedIncompleteOrderIds = useMemo(() => {
    const incomplete = new Set(
      summaries.filter(s => s.status !== 'Complete').map(s => s.order_id)
    )
    return selectedOrderIds.filter(id => incomplete.has(id))
  }, [summaries, selectedOrderIds])

  // Orders with bags still to print. With nothing selected, Generate Labels
  // File sends all of these.
  const needsPrintOrderIds = useMemo(
    () => summaries.filter(s => s.status !== 'Complete' && s.quantity_printed < s.total_items).map(s => s.order_id),
    [summaries]
  )
  const labelOrderIds = selectedOrderIds.length > 0 ? selectedIncompleteOrderIds : needsPrintOrderIds

  // Ready to pick: printed orders wait off the Scan page until released here.
  // With orders selected it releases the selected Printed ones, otherwise
  // every Printed order (usually the label run just made). Hold puts selected
  // Ready to Pick orders back.
  const setOrdersReadyToPick = useSetOrdersReadyToPick()
  const [readyBusy, setReadyBusy] = useState(false)
  const readyTargetIds = useMemo(() => {
    const printed = summaries.filter(s => s.status === 'Printed').map(s => s.order_id)
    if (selectedOrderIds.length === 0) return printed
    const selected = new Set(selectedOrderIds)
    return printed.filter(id => selected.has(id))
  }, [summaries, selectedOrderIds])
  const holdTargetIds = useMemo(() => {
    const selected = new Set(selectedOrderIds)
    return summaries.filter(s => s.status === 'Ready to Pick' && selected.has(s.order_id)).map(s => s.order_id)
  }, [summaries, selectedOrderIds])

  const handleSetReady = useCallback(
    async (ids: string[], ready: boolean, undo = false) => {
      if (ids.length === 0) return
      setReadyBusy(true)
      try {
        await setOrdersReadyToPick.trigger({ order_ids: ids, ready })
        await getAllOrderLines.trigger(undefined, { skipCache: true })
        setRowSelection({})
        const n = `${ids.length} order${ids.length !== 1 ? 's' : ''}`
        if (undo) {
          toast.success(ready ? `${n} back to Ready to pick` : `${n} held as Printed`)
          return
        }
        toast.success(ready ? `${n} ready to pick` : `${n} held as Printed`, {
          description: ready
            ? 'They show up on the Scan page now.'
            : 'They stay off the Scan page until marked Ready to pick.',
          action: { label: 'Undo', onClick: () => void handleSetReadyRef.current(ids, !ready, true) },
        })
      } catch (e) {
        toast.error(e instanceof Error && e.message ? e.message : 'Could not update the orders')
      } finally {
        setReadyBusy(false)
      }
    },
    [setOrdersReadyToPick, getAllOrderLines],
  )
  const handleSetReadyRef = useRef(handleSetReady)
  handleSetReadyRef.current = handleSetReady

  const openLabelsDialog = useCallback(async () => {
    setIncludeForecast(false)
    setLabelForecast(null)
    setLabelForecastError(null)
    setLabelsDialogOpen(true)
    setLabelForecastLoading(true)
    try {
      const result = (await getLabelForecast.trigger({})) as LabelForecast
      setLabelForecast(result)
    } catch (e) {
      setLabelForecastError(e instanceof Error ? e.message : 'Could not load the forecast')
    } finally {
      setLabelForecastLoading(false)
    }
  }, [getLabelForecast])

  const forecastBags = includeForecast ? labelForecast?.total ?? 0 : 0

  const handleGenerateLabelsFile = useCallback(async () => {
    if (labelOrderIds.length === 0 && forecastBags === 0) {
      toast.error('No orders need labels. Include the forecast to print it.')
      return
    }
    setLabelsFileLoading(true)
    try {
      await generateLabelsFile.trigger({
        orders: labelOrderIds,
        includeForecast: includeForecast && forecastBags > 0,
        forecastWeekStart: labelForecast?.week?.weekStart ?? null,
        reprintForecast: labelForecast?.printed != null,
      })
      const parts: string[] = []
      if (labelOrderIds.length > 0) parts.push(`${labelOrderIds.length} order(s)`)
      if (forecastBags > 0) parts.push(`${forecastBags} forecast bag(s)`)
      toast.success(`Labels file sent for ${parts.join(' + ')}`)
      setLabelsDialogOpen(false)
    } catch (e) {
      toast.error(e instanceof Error && e.message ? e.message : 'Failed to generate labels file')
    } finally {
      setLabelsFileLoading(false)
    }
  }, [labelOrderIds, generateLabelsFile, includeForecast, forecastBags, labelForecast])

  // Orders start unselected; Generate Labels File then covers every order
  // that still needs printing.

  const visibleSummaries = useMemo<OrderSummary[]>(
    () => summaries.filter(s => statusFilter.includes(s.status)),
    [summaries, statusFilter]
  )


  const toggleStatus = (status: OrderSummary['status']) =>
    setStatusFilter(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    )

  const expandedLines = useMemo<OrderLine[]>(
    () =>
      expandedOrderId === null
        ? []
        : allLines.filter(l => String(l.order_id) === expandedOrderId),
    [allLines, expandedOrderId]
  )

  // Summary stats
  const stats = useMemo(() => ({
    total: summaries.length,
    complete: summaries.filter(s => s.status === 'Complete').length,
    printed: summaries.filter(s => s.status === 'Printed').length,
    readyToPick: summaries.filter(s => s.status === 'Ready to Pick').length,
    inProgress: summaries.filter(s => s.status === 'In Progress').length,
    notStarted: summaries.filter(s => s.status === 'Not Started').length,
  }), [summaries])

  const columns = useMemo<ColumnDef<OrderSummary>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllRowsSelected()
                ? true
                : table.getIsSomeRowsSelected()
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={value => table.toggleAllRowsSelected(!!value)}
            onClick={e => e.stopPropagation()}
            aria-label="Select all orders"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={value => row.toggleSelected(!!value)}
            onClick={e => e.stopPropagation()}
            aria-label={`Select order ${row.original.order_id}`}
          />
        ),
      },
      {
        id: 'expander',
        header: '',
        cell: ({ row }) =>
          expandedOrderId === row.original.order_id ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          ),
      },
      {
        accessorKey: 'order_id',
        header: 'Order ID',
        cell: ({ row }) => (
          <span className="font-mono font-medium text-foreground">
            {row.original.order_id}
          </span>
        ),
      },
      {
        accessorKey: 'customer',
        header: 'Company',
        cell: ({ row }) => (
          // Long names wrap instead of widening the table
          <span className="text-foreground whitespace-normal inline-block min-w-[7rem] max-w-[16rem]">{row.original.customer || '—'}</span>
        ),
      },
      {
        header: 'Status',
        cell: ({ row }) => (
          <Badge
            variant={
              row.original.status === 'Complete'
                ? 'default'
                : row.original.status === 'Not Started'
                  ? 'outline'
                  : 'secondary'
            }
            className={cn(
              row.original.status === 'Printed' &&
                'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-950',
              row.original.status === 'Ready to Pick' &&
                'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200 hover:bg-violet-100 dark:hover:bg-violet-950'
            )}
          >
            {row.original.status}
          </Badge>
        ),
      },
      {
        accessorKey: 'total_items',
        header: 'Items',
        cell: ({ row }) => {
          const { total_items, weight_lbs, bags_without_weight } = row.original
          const weightText =
            weight_lbs === null
              ? null
              : `${weight_lbs.toFixed(1)} lbs${bags_without_weight > 0 ? '*' : ''}`
          const weightNote =
            weight_lbs === null
              ? 'Weight shows once products load'
              : bags_without_weight > 0
                ? `${weightText}. ${bags_without_weight} bag(s) have no weight set on their product, so this is low.`
                : weightText
          return (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="tabular-nums cursor-help underline decoration-dotted decoration-muted-foreground/50 underline-offset-4">
                    {total_items}
                    {bags_without_weight > 0 && weight_lbs !== null && (
                      <span className="text-amber-600 dark:text-amber-400">*</span>
                    )}
                    {/* No hover on phones, so the weight shows inline on cards */}
                    {weightText && (
                      <span className="sm:hidden text-muted-foreground"> · {weightText}</span>
                    )}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">{weightNote}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        },
      },
      { accessorKey: 'quantity_printed', header: 'Printed' },
      { accessorKey: 'quantity_picked', header: 'Picked' },
      { accessorKey: 'quantity_remaining', header: 'Remaining' },
      {
        accessorKey: 'progress',
        header: 'Progress',
        cell: ({ row }) => <ProgressBar value={row.original.progress} />,
      },
      {
        id: 'action',
        header: '',
        cell: ({ row }) => {
          const o = row.original
          const open = o.status !== 'Complete'
          const busy =
            picklistOrderId === o.order_id ||
            singleLabelOrderId === o.order_id ||
            deletingOrderId === o.order_id
          if (!open && !o.customer_id) return null
          return (
            <div className="flex justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                    aria-label={`Actions for order ${o.order_id}`}
                    onClick={e => e.stopPropagation()}
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreHorizontal className="w-4 h-4" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
                  {open && (
                    <DropdownMenuItem
                      onSelect={() => navigate(`/scan?order=${encodeURIComponent(o.order_id)}`)}
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Pick
                    </DropdownMenuItem>
                  )}
                  {open && (
                    <DropdownMenuItem
                      disabled={picklistOrderId !== null}
                      onSelect={() => void handleGeneratePicklist(o.order_id)}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Picklist
                    </DropdownMenuItem>
                  )}
                  {!o.isShippingCustomer && o.customer_id && (
                    <DropdownMenuItem
                      onSelect={() => void openToteLabelDialog(o.customer_id!, o.customer)}
                    >
                      <QrCode className="w-4 h-4 mr-2" />
                      Tote Labels
                    </DropdownMenuItem>
                  )}
                  {o.isShippingCustomer && o.customer_id && (
                    <DropdownMenuItem
                      disabled={singleLabelOrderId !== null}
                      onSelect={() => void handlePrintSingleToteLabel(o.order_id, o.customer_id!, o.customer)}
                    >
                      <QrCode className="w-4 h-4 mr-2" />
                      Tote Label
                    </DropdownMenuItem>
                  )}
                  {open && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => setOrderToComplete(o.order_id)}>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Complete
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        disabled={deletingOrderId === o.order_id}
                        onSelect={() => void handleDeleteOrder(o.order_id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        },
      },
    ],
    [navigate, expandedOrderId, picklistOrderId, handleGeneratePicklist, openToteLabelDialog, singleLabelOrderId, handlePrintSingleToteLabel, deletingOrderId, handleDeleteOrder]
  )

  const table = useReactTable({
    data: visibleSummaries,
    columns,
    state: { sorting, globalFilter: filterValue, rowSelection },
    getRowId: row => row.order_id,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onGlobalFilterChange: setFilterValue,
    globalFilterFn: (row, _columnId, value) => `${row.original.order_id} ${row.original.customer}`.toLowerCase().includes(String(value).toLowerCase()),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  return (
    <div style={{ padding: '8px 12px' }} className="flex flex-col gap-4 min-h-screen bg-background overflow-y-auto">
      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 border rounded-lg p-4 bg-card">
        {([
          { label: 'Total Orders', value: stats.total },
          { label: 'Complete', value: stats.complete },
          { label: 'Printed', value: stats.printed },
          { label: 'Ready to Pick', value: stats.readyToPick },
          { label: 'In Progress', value: stats.inProgress },
          { label: 'Not Started', value: stats.notStarted },
        ] as const).map(s => (
          <div key={s.label} className="flex flex-col items-center p-3 rounded-md bg-background border">
            <span className="text-xs text-muted-foreground uppercase tracking-wide text-center">{s.label}</span>
            <span className="text-3xl font-bold text-foreground mt-1">{s.value}</span>
          </div>
        ))}
      </div>

      {/* Table card */}
      <div className="border rounded-lg p-4 bg-card flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="text-xl font-semibold text-foreground">{statusFilter.length === ALL_STATUSES.length ? 'All orders' : statusFilter.length === ACTIVE_STATUSES.length && !statusFilter.includes('Complete') ? 'Active orders' : 'Filtered orders'}</h2>
          {/* View controls stay separate from actions on selected orders. */}
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 min-w-0 sm:flex-none">
              <Filter className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                className="pl-8 text-sm w-full sm:w-64"
                placeholder="Search order or customer…"
                aria-label="Search orders by order number or customer"
                value={filterValue}
                onChange={e => setFilterValue(e.target.value)}
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" aria-label="Status">
                  <ListFilter className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Status</span>
                  {statusFilter.length < ALL_STATUSES.length && (
                    <Badge variant="secondary" className="ml-1">{statusFilter.length}</Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Show statuses</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {ALL_STATUSES.map(status => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={statusFilter.includes(status)}
                    onCheckedChange={() => toggleStatus(status)}
                    onSelect={e => e.preventDefault()}
                  >
                    {status}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void getAllOrderLines.trigger(undefined, { skipCache: true })}
              disabled={getAllOrderLines.loading}
              aria-label="Refresh"
            >
              <RefreshCw className={cn('w-4 h-4', getAllOrderLines.loading && 'animate-spin')} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm"><MoreHorizontal className="w-4 h-4 mr-1" />Tools{lastImportFailed && <span className="ml-1 text-destructive">!</span>}</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled={importing} onSelect={() => void handleImportOrders()}>{importing ? 'Importing…' : 'Import orders'}</DropdownMenuItem>
                <DropdownMenuItem disabled={unpickedPdfLoading} onSelect={() => void handleGenerateUnpickedPdf()}>Items to pick PDF (all orders)</DropdownMenuItem>
                {selectedOrderIds.length === 0 && <>
                  <DropdownMenuItem disabled={readyBusy || readyTargetIds.length === 0} onSelect={() => void handleSetReady(readyTargetIds, true)}>Mark all printed orders ready ({readyTargetIds.length})</DropdownMenuItem>
                  <DropdownMenuItem disabled={labelsFileLoading} onSelect={() => void openLabelsDialog()}>Generate labels for all orders needing labels ({needsPrintOrderIds.length})</DropdownMenuItem>
                </>}
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm" aria-label="Active filters">
          <span className="text-muted-foreground">Statuses:</span>
          {statusFilter.map(status => <Button key={status} variant="secondary" size="sm" onClick={() => toggleStatus(status)} aria-label={`Remove ${status} filter`}>{status}<span className="ml-2" aria-hidden="true">×</span></Button>)}
          {statusFilter.length === 0 && <span>No statuses selected</span>}
          <Button variant="ghost" size="sm" onClick={() => { setStatusFilter(ALL_STATUSES); setFilterValue('') }}>Show all</Button>
          <Button variant="ghost" size="sm" onClick={() => setStatusFilter(ACTIVE_STATUSES)}>Active only</Button>
          {filterValue && <Button variant="ghost" size="sm" onClick={() => setFilterValue('')}>Clear search</Button>}
        </div>
        {lastImport && <p className={cn('text-xs', lastImportFailed ? 'text-destructive' : 'text-muted-foreground')}>
          {lastImportFailed ? `Last import failed ${timeAgo(lastImport.lastRun?.finished_at)}. Retry from Tools.` : `Imported ${timeAgo(lastImport.lastSuccess?.finished_at)}`}
        </p>}
        {selectedOrderIds.length > 0 && <div className="flex flex-wrap items-center gap-2 rounded-md border bg-accent/40 p-3" role="region" aria-label="Selected order actions">
          <span className="text-sm font-medium">{selectedOrderIds.length} orders selected</span>
          <Button variant="ghost" size="sm" onClick={() => setRowSelection({})}>Clear selection</Button>
          <Button size="sm" disabled={readyBusy || readyTargetIds.length === 0} onClick={() => void handleSetReady(readyTargetIds, true)}>Mark ready ({readyTargetIds.length})</Button>
          <Button variant="outline" size="sm" disabled={labelsFileLoading || selectedIncompleteOrderIds.length === 0} onClick={() => void openLabelsDialog()}>Generate labels ({selectedIncompleteOrderIds.length})</Button>
          {holdTargetIds.length > 0 && <Button variant="outline" size="sm" disabled={readyBusy} onClick={() => void handleSetReady(holdTargetIds, false)}>Hold ({holdTargetIds.length})</Button>}
        </div>}

        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map(hg => (
                <TableRow key={hg.id}>
                  {hg.headers.map(header => (
                    <TableHead
                      key={header.id}
                      className={cn(
                        'whitespace-nowrap',
                        header.column.getCanSort() && 'cursor-pointer select-none'
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() === 'asc'
                        ? ' ↑'
                        : header.column.getIsSorted() === 'desc'
                          ? ' ↓'
                          : ''}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center text-muted-foreground py-12">
                    {getAllOrderLines.loading ? 'Loading…' : 'No orders found'}
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map(row => {
                  const isExpanded = expandedOrderId === row.original.order_id
                  return (
                    <Fragment key={row.id}>
                      <TableRow
                        className={cn(
                          'cursor-pointer hover:bg-accent/50 transition-colors',
                          row.original.status === 'Complete' && 'bg-green-50 dark:bg-green-950/30',
                          isExpanded && 'bg-accent/40'
                        )}
                        onClick={() =>
                          setExpandedOrderId(prev =>
                            prev === row.original.order_id ? null : row.original.order_id
                          )
                        }
                      >
                        {row.getVisibleCells().map(cell => (
                          <TableCell key={cell.id} className="whitespace-nowrap">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                      {isExpanded && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={columns.length} className="p-0">
                            <OrderLineItems
  lines={expandedLines}
  savingKey={savingKey}
  deletingLineId={deletingLineId}
  onUpdate={(line, field, value) =>
    void handleUpdateLine(line, field, value)
  }
  onDelete={line => void handleDeleteLine(line)}
  onOpenProduct={line => void handleOpenProduct(line)}
  openingSku={openingSku}
  weightOzBySku={weightOzBySku}
/>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>

        <p className="text-xs text-muted-foreground">
          {table.getFilteredRowModel().rows.length} order(s)
        </p>
      </div>

      <Dialog
        open={labelTarget !== null}
        onOpenChange={open => {
          if (!open && !printingLabels) setLabelTarget(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Print Tote Labels</DialogTitle>
            <DialogDescription>
              {labelTarget?.customer || 'Customer'} — enter the number of label copies to print.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="tote-copies">Number of copies</Label>
            <Input
              id="tote-copies"
              type="number"
              min={1}
              value={labelCopies}
              disabled={loadingSuggestion}
              onChange={e => setLabelCopies(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {loadingSuggestion
                ? 'Loading suggested number of totes…'
                : 'Defaulted to the suggested number of totes for the open order size.'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLabelTarget(null)} disabled={printingLabels}>
              Cancel
            </Button>
            <Button onClick={() => void handlePrintToteLabels()} disabled={printingLabels || loadingSuggestion}>
              {printingLabels ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Printing…
                </>
              ) : (
                'Print'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={labelsDialogOpen}
        onOpenChange={open => {
          if (!open && !labelsFileLoading) setLabelsDialogOpen(false)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Labels File</DialogTitle>
            <DialogDescription>
              {selectedIncompleteOrderIds.length > 0
                ? `Labels for ${selectedIncompleteOrderIds.length} selected order(s).`
                : needsPrintOrderIds.length > 0
                  ? `Labels for all ${needsPrintOrderIds.length} order(s) that still need printing. Select orders first to print only those.`
                  : 'No orders need printing right now.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            {labelForecastLoading ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading forecast…
              </p>
            ) : labelForecastError ? (
              <p className="text-sm text-destructive">Couldn't load the forecast: {labelForecastError}</p>
            ) : !labelForecast?.week ? (
              <p className="text-sm text-muted-foreground">
                No forecast yet. Generate one from the Dashboard to include it.
              </p>
            ) : labelForecast.stale ? (
              <p className="text-sm text-muted-foreground">
                The saved forecast is for {formatForecastWeek(labelForecast.week)}, which has already
                started. Regenerate it from the Dashboard or the Production tab to include next
                week&apos;s forecast.
              </p>
            ) : (
              <>
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="labels-include-forecast"
                    checked={includeForecast}
                    disabled={labelForecast.total === 0}
                    onCheckedChange={v => setIncludeForecast(v === true)}
                  />
                  <Label htmlFor="labels-include-forecast" className="leading-snug">
                    Include forecast for {formatForecastWeek(labelForecast.week)} ({labelForecast.total} bag
                    {labelForecast.total === 1 ? '' : 's'})
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground"><HelpText>
                  Same forecast as Production, rounded to tier minimums. Forecast bags are added to the
                  file but aren't marked printed on any order.
                </HelpText></p>
                {labelForecast.printed && (
                  <p className="text-sm rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                    This week's forecast was already printed{' '}
                    {new Date(labelForecast.printed.printed_at).toLocaleString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                    {labelForecast.printed.printed_by ? ` by ${labelForecast.printed.printed_by}` : ''} (
                    {labelForecast.printed.total_bags} bags). Including it again prints those bags again.
                  </p>
                )}
                {includeForecast && labelForecast.items.length > 0 && (
                  <div className="max-h-40 overflow-y-auto rounded-md border text-xs">
                    {labelForecast.items.map(i => (
                      <div key={i.sku} className="flex justify-between gap-2 px-2 py-1 border-b last:border-b-0">
                        <span className="truncate">{i.item_name ?? i.sku}</span>
                        <span className="tabular-nums">{i.quantity}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLabelsDialogOpen(false)} disabled={labelsFileLoading}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleGenerateLabelsFile()}
              disabled={
                labelsFileLoading ||
                labelForecastLoading ||
                (labelOrderIds.length === 0 && forecastBags === 0)
              }
            >
              {labelsFileLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Sending…
                </>
              ) : labelForecast?.printed && includeForecast ? (
                'Print again'
              ) : (
                'Generate'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={orderToComplete !== null} onOpenChange={open => { if (!open) setOrderToComplete(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark order {orderToComplete} complete?</AlertDialogTitle>
            <AlertDialogDescription>
              This sets order {orderToComplete} to complete and removes any line items that were never picked. Removed items stay in Settings › Recently deleted for 30 days.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={completeOrder.loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => {
                e.preventDefault()
                void handleCompleteOrder()
              }}
              disabled={completeOrder.loading}
            >
              {completeOrder.loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
              Complete Order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {productTarget && (
        <ProductFormDialog
          open={productOpen}
          onOpenChange={setProductOpen}
          product={productTarget}
          onSaved={() => {
            void getProducts.trigger(undefined, { skipCache: true })
            window.dispatchEvent(new Event(PRODUCTS_CHANGED))
          }}
        />
      )}
    </div>
  )
}
